import React from 'react';
import { ArrowRight, Globe, Users, Zap, Shield } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const stats = [
    { icon: Globe, label: 'Global Reach', value: '4+ Regions' },
    { icon: Users, label: 'Expert Team', value: '50+ Engineers' },
    { icon: Zap, label: 'Technologies', value: '10+ Skills' },
    { icon: Shield, label: 'Experience', value: '10+ Years' }
  ];

  return (
    <section id="home" className="pt-16 pb-20 bg-gradient-to-br from-slate-50 via-purple-50/30 to-blue-50 relative overflow-hidden">
      {/* Tech Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-64 h-64 border border-purple-300 rounded-full"></div>
        <div className="absolute top-40 right-20 w-32 h-32 border border-cyan-300 rounded-lg rotate-45"></div>
        <div className="absolute bottom-20 left-1/4 w-48 h-48 border border-purple-200 rounded-full"></div>
        <div className="absolute top-1/3 right-1/3 w-24 h-24 border border-cyan-200 rounded-lg rotate-12"></div>
        {/* Grid pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(139, 69, 193, 0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(139, 69, 193, 0.03) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center min-h-[85vh]">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-100 to-purple-100 text-purple-800 rounded-full text-sm font-medium border border-cyan-300 shadow-lg shadow-cyan-500/25 animate-pulse">
                <Globe className="w-4 h-4 mr-2 text-cyan-600" />
                Serving Globally
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-bold leading-tight">
                <span className="block text-transparent bg-gradient-to-r from-purple-600 via-cyan-500 to-violet-500 bg-clip-text drop-shadow-lg animate-pulse">
                  Empowering Your
                </span>
                <span className="block text-transparent bg-gradient-to-r from-purple-600 via-cyan-500 to-violet-500 bg-clip-text drop-shadow-lg animate-pulse">
                  Digital Future
                </span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-600 leading-relaxed max-w-3xl">
                Leading IT outsourcing and consulting company delivering cutting-edge solutions 
                in software development, healthtech, and algo trading web and mobile apps for clients across 
                US, UK, EU, MENA, and beyond.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-6">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white px-10 py-4 text-xl shadow-lg shadow-cyan-500/30 hover:shadow-xl hover:shadow-cyan-500/50 transform hover:scale-105 transition-all duration-300 border border-cyan-300/50"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                Get Started
                <ArrowRight className="w-6 h-6 ml-3" />
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="border-2 border-cyan-400 text-purple-600 hover:bg-gradient-to-r hover:from-purple-600 hover:to-cyan-500 hover:text-white px-10 py-4 text-xl hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-300 transform hover:scale-105 bg-white/80 backdrop-blur-sm"
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              >
                View Our Services
              </Button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 pt-8">
              {stats.map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="flex justify-center mb-2">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/20 border border-purple-200">
                        <IconComponent className="w-6 h-6 text-purple-600 drop-shadow-sm" />
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 drop-shadow-sm">{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Visual Element */}
          <div className="relative w-full flex items-center justify-center lg:justify-end">
            <h2 className="sr-only">Company highlights</h2>
            <div className="relative z-10 bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-cyan-200/50 max-w-md w-full mx-auto lg:mx-0">
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-violet-400 rounded-lg flex items-center justify-center shadow-lg">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">Innovation-Driven</h3>
                    <p className="text-gray-600 text-sm">Cutting-edge solutions</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-teal-400 rounded-lg flex items-center justify-center shadow-lg">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">Global Presence</h3>
                    <p className="text-gray-600 text-sm">Worldwide clientele</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-violet-500 to-purple-400 rounded-lg flex items-center justify-center shadow-lg">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-transparent bg-gradient-to-r from-purple-600 to-cyan-500 bg-clip-text">Expert Team</h3>
                    <p className="text-gray-600 text-sm">Skilled professionals</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Background decorative elements */}
            <div className="absolute inset-0 -z-10">
              <div className="absolute top-4 right-4 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
              <div className="absolute bottom-4 left-4 w-72 h-72 bg-cyan-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
